import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import AudioCard from '@/components/AudioCard';
import WaveLoader from '@/components/WaveLoader';
import { getCategory, getTracks, getFavorites } from '@/lib/api';
import { toast } from 'sonner';

export default function CategoryPage() {
  const { category: categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [categorySlug]);

  const loadData = async () => {
    try {
      const [catRes, tracksRes, favRes] = await Promise.all([
        getCategory(categorySlug),
        getTracks(categorySlug),
        getFavorites(),
      ]);

      setCategory(catRes.data);
      setTracks(tracksRes.data);
      setFavorites(favRes.data.map(t => t.id));
    } catch (error) {
      console.error('Error loading category:', error);
      toast.error('Failed to load category');
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteChange = (trackId, isFavorite) => {
    if (isFavorite) {
      setFavorites([...favorites, trackId]);
    } else {
      setFavorites(favorites.filter(id => id !== trackId));
    }
  };

  if (loading) {
    return <WaveLoader />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 pb-24 md:pb-8 md:pt-24">
      <Navigation />

      <main className="container mx-auto px-4 md:px-6 py-8">
        {/* Back Button */}
        <Link 
          to="/library" 
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-6"
          data-testid="back-to-library"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Back to Library</span>
        </Link>

        {/* Category Header */}
        <div className="relative rounded-2xl overflow-hidden mb-10 h-48 md:h-64" data-testid="category-header">
          <img 
            src={category?.image_url}
            alt={category?.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
            <h1 className="text-3xl md:text-5xl font-serif text-white mb-2">{category?.name}</h1>
            <p className="text-zinc-400 max-w-xl">{category?.description}</p>
            <p className="text-sm text-zinc-600 mt-3">{tracks.length} tracks available</p>
          </div>
        </div>

        {/* Tracks Grid */}
        <section data-testid="category-tracks-grid">
          {tracks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-500">No tracks in this category yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {tracks.map((track) => (
                <AudioCard 
                  key={track.id} 
                  track={track}
                  isFavorite={favorites.includes(track.id)}
                  onFavoriteChange={handleFavoriteChange}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
